import { useEffect, useRef } from 'react';
import { obtenirSdk } from './sdk';

/**
 * Affiche le film en cours dans le statut Discord des participants.
 *
 * C'est ce qui donne une existence à la séance en dehors du salon vocal : la
 * liste des membres montre « Regarde Interstellar », et quelqu'un qui passe
 * par là sait qu'il y a une séance sans avoir à demander.
 *
 * Deux choix qui ne se voient pas mais se remarqueraient :
 *
 * L'horodatage n'est posé **que pendant la lecture**. Discord fait défiler un
 * compteur à partir de `start` sans jamais s'arrêter : laissé en place pendant
 * une pause, il annoncerait une progression qui n'a pas lieu.
 *
 * `start` est calculé à rebours depuis la position du film, pas depuis
 * l'instant où l'on a rejoint. Quelqu'un qui arrive à la moitié voit donc
 * « 1:04:12 » comme tout le monde, et non « 3 secondes ».
 *
 * L'échec est sans conséquence : si la permission manque, le statut ne
 * s'affiche pas et la séance continue. Rien ici ne mérite d'interrompre un
 * film.
 */
export function usePresence({ film, enLecture, position, nomDuMaitre, enDirect = false }) {
  // La position bouge en permanence ; la relire dans une ref évite de
  // réémettre un statut identique à chaque seconde de lecture.
  const positionRef = useRef(position);
  positionRef.current = position;

  const prevenuRef = useRef(false);

  useEffect(() => {
    const poser = async () => {
      const sdk = await obtenirSdk();

      try {
        if (!film) {
          await sdk.commands.setActivity({ activity: null });
          return;
        }

        const debut = Date.now() - Math.max(0, positionRef.current ?? 0) * 1000;

        await sdk.commands.setActivity({
          activity: {
            type: 3, // « Regarde »
            details: film.title,
            // Un direct n'est la séance de personne : il n'a pas de maître à
            // nommer, et le compteur qui défile mesure le temps d'antenne, pas
            // une progression dans un fichier.
            state: enDirect
              ? '🔴 En direct'
              : nomDuMaitre ? `Séance de ${nomDuMaitre}` : 'Séance partagée',
            ...(enLecture ? { timestamps: { start: Math.round(debut) } } : {}),
            instance: true,
          },
        });
      } catch (erreur) {
        // Une seule fois : le statut est un agrément, pas une fonction, et
        // répéter l'échec à chaque changement noierait la console.
        if (!prevenuRef.current) {
          prevenuRef.current = true;
          console.warn('Statut Discord non mis à jour :', erreur?.message ?? erreur);
        }
      }
    };

    poser();
    // Volontairement sans nettoyage : effacer ici repartirait à chaque mise en
    // pause, et la remise en place pourrait arriver avant l'effacement. Le
    // statut se met à jour, il ne se retire pas entre deux états.
  }, [film?.id, film?.title, enLecture, nomDuMaitre, enDirect]);

  // L'effacement n'a qu'un moment légitime : quand on quitte l'Activity. Sans
  // lui, le statut resterait figé sur un film que la personne ne regarde plus.
  useEffect(() => () => {
    obtenirSdk()
      .then((sdk) => sdk.commands.setActivity({ activity: null }).catch(() => {}))
      .catch(() => {});
  }, []);
}
